<template>
  <a
    tabindex="0"
    :href="notification.notificationLink"
    data-test="user-notification"
    class="notification-item"
    @click.prevent="handleClick(notification)"
  >
    <NotificationImage :notification="notification" />
    <div>
      <span
        v-html="appendTimeAgo(notification)"
        class="message-text"
        data-test="notification-message"
      ></span>
    </div>
    <Tooltip
      v-model="showTooltip"
      :text="tooltipText"
      :target="`#tooltip-${notification.id}`"
      :key="notification.id"
      data-test="notification-tooltip"
      trigger="manual"
    />
    <button
      :id="`tooltip-${notification.id}`"
      class="status"
      type="button"
      @mouseover.stop.prevent="showTooltip = true"
      @mouseleave.stop.prevent="showTooltip = false"
      @focus.stop.prevent="showTooltip = true"
      @blur.stop.prevent="showTooltip = false"
      @click.stop.prevent="updateStatus(notification)"
      data-test="status-update-button"
    >
      <span
        class="status-center"
        v-show="notification.status === 'Unread'"
        data-test="unread"
      ></span>
    </button>
  </a>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Tooltip } from "uiv";
import timeAgo from "../../services/utilities/time-ago";
import { NotificationApiModel } from "../../api-models";
import NotificationImage from "./notification-image.vue";

const props = defineProps<{ notification: NotificationApiModel }>();

const emit = defineEmits(["update-status"]);

const showTooltip = ref(false);

const tooltipText = computed(() => {
  if (props.notification.status === "Read") {
    return "Mark as unread";
  }

  return "Mark as read";
});

const appendTimeAgo = (notification: NotificationApiModel): string => {
  const closingTagIndex = notification.message.lastIndexOf("</p>");
  if (closingTagIndex === -1) {
    return notification.message;
  }
  const messageStart = notification.message.substring(0, closingTagIndex);
  const messageEnd = notification.message.substring(closingTagIndex);
  return `${messageStart} <span class="time-ago">&nbsp;${timeAgo(
    notification.createdDate,
  )}</span>${messageEnd}`;
};

const updateStatus = (notification: NotificationApiModel) => {
  const newStatus = notification.status === "Read" ? "Unread" : "Read";
  emit("update-status", notification, newStatus);
};

const handleClick = (notification: NotificationApiModel) => {
  if (notification.notificationLink) {
    const url = new URL(notification.notificationLink);
    const params = new URLSearchParams(url.search);

    // Set relevant teamId if it exists
    if (notification.teamId) {
      params.set("teamId", notification.teamId);
    }

    if (notification.status !== "Read") {
      updateStatus(props.notification);
    }

    url.search = params.toString();
    window.location.href = url.toString().replace("5000", "8080");
  }
};
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.notification-item {
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 10px;
  margin-bottom: 10px;
}

.notification-item .message-text {
  display: inline;
  white-space: normal;
}

.status {
  min-width: 20px;
  border-radius: 100vmax;
  background-color: transparent;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus {
    outline: 1px solid @form-text-color;
  }
}

.status-center {
  width: 8px;
  aspect-ratio: 1;
  border-radius: 100vmax;
  background-color: @brand-primary;
}

.notification-action {
  padding: 5px 10px;
  cursor: pointer;

  &:hover,
  &:focus {
    background-color: #f5f5f5;
  }
}
</style>

<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

span.message-text > p > span.time-ago {
  color: @form-text-color;
}

.notification-item .message-text {
  color: @off-black;

  p {
    margin: 0;
  }
}

.dropdown {
  .btn.btn-trigger {
    box-shadow: none;

    &:focus {
      box-shadow: none;
    }
  }
}

.dropdown-toggle {
  color: #8f8f8f;
  display: block;

  &:hover,
  &:focus {
    color: #404040;
  }
}

.dropdown-selected {
  background-size: 100% 100%;
  text-decoration: none;
}
</style>
